import type {ContentRelationshipField, KeyTextField, RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import Image from 'next/image'
import type {AuthorDocumentData, PickContentRelationshipFieldData, Simplify} from 'prismicio-types'
import type {FC} from 'react'

type ArticleHeaderProps = {
  title: RichTextField
  author?: ContentRelationshipField<
    'author',
    string,
    PickContentRelationshipFieldData<
      {id: 'author'; fields: ['photo', 'name', 'summary', 'linkedin_link']},
      Simplify<AuthorDocumentData>,
      string
    >
  >
  authorTag?: KeyTextField
  category?: string | null | undefined
  formattedDate: string
  readingTime?: KeyTextField
}

export const ArticleHeader: FC<ArticleHeaderProps> = ({
  title,
  author,
  authorTag,
  category,
  formattedDate,
  readingTime,
}) => (
  <>
    <PrismicRichText
      field={title}
      components={{
        heading1: ({children}) => (
          <h1 className="text-32 font-medium leading-normal tracking-[-.32px] text-moss-black md:text-36 md:leading-[42px]">
            {children}
          </h1>
        ),
      }}
    />

    <div className="flex flex-wrap items-center justify-between gap-4">
      {isFilled.contentRelationship(author) && (
        <div className="flex gap-3">
          <Image
            src={(author.data as AuthorDocumentData)?.photo.url || ''}
            className="size-10 rounded-full object-cover"
            alt={(author.data as AuthorDocumentData)?.photo.alt || 'Author image'}
            width={40}
            height={40}
          />
          <div className="flex flex-col self-end">
            {authorTag && (
              <span className="text-13 font-medium leading-normal tracking-[.13px] text-moss-grey-300">
                {authorTag}
              </span>
            )}
            <span className="text-13 font-medium leading-normal tracking-[.13px] text-moss-black">
              {(author.data as AuthorDocumentData)?.name}
            </span>
          </div>
        </div>
      )}

      <div className="flex flex-col items-end gap-1 self-end">
        {formattedDate && <span className="text-detail text-moss-black">{formattedDate}</span>}
        <div className="flex gap-4">
          {category && <span className="text-detail text-moss-grey-300">{category}</span>}
          {readingTime && <span className="text-detail text-moss-grey-300">{readingTime}</span>}
        </div>
      </div>
    </div>
  </>
)
